import intentData from './lexical/intent.json';
import { normalizeSearchTerm } from './utils';

export interface Intent {
  primaryIntent: string;
  secondaryIntents: string[];
  featureKeywords: string[];
  exclusionTags: string[];
  sectionIds: string[];
}

interface IntentData {
  intents: Intent[];
}

const intents: Intent[] = (intentData as IntentData).intents;

const ACTION_WORDS = {
  disable: ['disable', 'turn off', 'deactivate', 'switch off', 'off', 'stop'],
  adjust: ['adjust', 'change', 'modify', 'set', 'configure', 'update'],
  explain: ['explain', 'how', 'what', 'about', 'information', 'details'],
};

export function detectActionType(
  query: string
): 'disable' | 'adjust' | 'explain' | null {
  const normalizedQuery = normalizeSearchTerm(query).toLowerCase();

  for (const actionWord of ACTION_WORDS.disable) {
    if (normalizedQuery.includes(actionWord)) {
      return 'disable';
    }
  }

  for (const actionWord of ACTION_WORDS.adjust) {
    if (normalizedQuery.includes(actionWord)) {
      return 'adjust';
    }
  }

  for (const actionWord of ACTION_WORDS.explain) {
    if (normalizedQuery.includes(actionWord)) {
      return 'explain';
    }
  }

  return null;
}

export function findMatchingIntent(
  query: string
): { intent: Intent; matchedFeatureKeyword: string } | null {
  const normalizedQuery = normalizeSearchTerm(query).toLowerCase();
  const queryWords = normalizedQuery.split(/\s+/).filter((w) => w.length > 0);

  for (const intent of intents) {
    for (const featureKeyword of intent.featureKeywords) {
      const normalizedKeyword = featureKeyword.toLowerCase();

      if (normalizedQuery.includes(normalizedKeyword)) {
        return { intent, matchedFeatureKeyword: featureKeyword };
      }

      const keywordWords = normalizedKeyword.split(/\s+/);
      if (keywordWords.length > 1) {
        // Require all keyword words to match
        // Prevent single-letter words from matching longer words (e.g., "s" matching "seat")
        const allWordsMatch = keywordWords.every((keywordWord) =>
          queryWords.some((queryWord) => {
            // Exact match always works
            if (queryWord === keywordWord) return true;

            // Prevent very short words (1-2 chars) from matching longer words via substring
            // This prevents "s" from matching "seat" or "heating"
            if (queryWord.length <= 2 && keywordWord.length > 2) return false;
            if (keywordWord.length <= 2 && queryWord.length > 2) return false;

            // For words 3+ chars, allow substring matching
            if (queryWord.length >= 3 && keywordWord.length >= 3) {
              return (
                queryWord.includes(keywordWord) ||
                keywordWord.includes(queryWord)
              );
            }

            return false;
          })
        );
        if (allWordsMatch) {
          return { intent, matchedFeatureKeyword: featureKeyword };
        }
      }
    }
  }

  for (const intent of intents) {
    for (const featureKeyword of intent.featureKeywords) {
      const normalizedKeyword = featureKeyword.toLowerCase();
      const keywordWords = normalizedKeyword.split(/\s+/);

      if (keywordWords.length === 1) {
        const keywordWord = keywordWords[0];
        if (
          queryWords.some((queryWord) => {
            // Exact match always works
            if (queryWord === keywordWord) return true;

            // Prevent very short words (1-2 chars) from matching longer words via substring
            if (queryWord.length <= 2 && keywordWord.length > 2) return false;
            if (keywordWord.length <= 2 && queryWord.length > 2) return false;

            // For words 3+ chars, allow substring matching
            if (queryWord.length >= 3 && keywordWord.length >= 3) {
              return (
                queryWord.includes(keywordWord) ||
                keywordWord.includes(queryWord)
              );
            }

            return false;
          })
        ) {
          return { intent, matchedFeatureKeyword: featureKeyword };
        }
      }
    }
  }

  return null;
}

export function sectionMatchesFeatureKeywords(
  section: {
    sectionTitle?: { plaintext?: string | null } | null;
    features?: any[] | null;
  },
  featureKeywords: string[]
): boolean {
  const sectionTitle = section.sectionTitle?.plaintext?.toLowerCase() || '';

  for (const keyword of featureKeywords) {
    const normalizedKeyword = keyword.toLowerCase();
    if (sectionTitle.includes(normalizedKeyword)) {
      return true;
    }
  }

  if (section.features) {
    for (const feature of section.features) {
      if (!feature) continue;

      if (feature.title?.plaintext) {
        const featureTitle = feature.title.plaintext.toLowerCase();
        for (const keyword of featureKeywords) {
          const normalizedKeyword = keyword.toLowerCase();
          if (featureTitle.includes(normalizedKeyword)) {
            return true;
          }
        }
      }

      if (feature.content) {
        for (const contentItem of feature.content) {
          const plaintext = contentItem?.plaintext?.toLowerCase() || '';
          const markdown = contentItem?.markdown?.toLowerCase() || '';
          const combined = plaintext + ' ' + markdown;

          for (const keyword of featureKeywords) {
            const normalizedKeyword = keyword.toLowerCase();
            if (combined.includes(normalizedKeyword)) {
              return true;
            }
          }
        }
      }

      if (feature.caption?.plaintext) {
        const caption = feature.caption.plaintext.toLowerCase();
        for (const keyword of featureKeywords) {
          const normalizedKeyword = keyword.toLowerCase();
          if (caption.includes(normalizedKeyword)) {
            return true;
          }
        }
      }
    }
  }

  return false;
}

export function getFeatureKeywordsFromIntent(intent: Intent): string[] {
  return intent.featureKeywords;
}
